<template>
  <div>
    <div class="entry-form-container" v-if="showVerification">
      <verify-email
        :header="$t('auth.forms.loginForm.messages.emailIsUnverified')"
        :login="true"
        @backToLogin="showVerification = false"
        :email="creds.email" />
    </div>

    <template v-else>
      <incorrect-portal-dialog
        :email="portalEmail"
        :show-dialog="showIncorrectPortalDialog"
        @close="showIncorrectPortalDialog = false"
        external-activator />
      <div class="entry-form-container">
        <v-container fluid class="px-1">
          <!-- TODO Vue3: These tabs are supposed to have a grey underline on the inactive item -->
          <!-- TODO Vue3: You can see the problem in main.scss ~line:324 -->
          <v-tabs grow v-model="tab" class="non-active-tab-underline" color="primary">
            <v-tab class="flex-grow-1 font-weight-bold">
              <v-icon class="mr-2">mdi-warehouse</v-icon>
              {{ $t('auth.pages.login.tabLabels.warehouseTab') }}
            </v-tab>
            <v-tab class="flex-grow-1 font-weight-bold">
              <v-icon class="mr-2">mdi-truck</v-icon>
              {{ $t('auth.pages.login.tabLabels.carrierTab') }}
            </v-tab>
          </v-tabs>
        </v-container>
      </div>

      <v-tabs-window v-model="tab">
        <v-tabs-window-item>
          <!-- TODO Vue3: There must be a way to get the labels above outlined fields, but I can't find it -->
          <v-form
            ref="form"
            @keyup.enter="submit"
            v-if="!loggedIn"
            class="entry-form-container px-1 mt-2"
            data-testid="login-form">
            <v-text-field
              variant="outlined"
              data-testid="login-form-email-input"
              :required="true"
              density="comfortable"
              type="email"
              v-model="creds.email"
              :rules="[
                ...$validator.rules.required($t('auth.forms.loginForm.labels.emailField')),
                ...$validator.rules.email
              ]"
              :label="$t('auth.forms.loginForm.labels.emailField')"
              :placeholder="$t('auth.forms.loginForm.placeholders.emailField')" />

            <v-text-field
              variant="outlined"
              data-testid="login-form-password-input"
              :required="true"
              density="comfortable"
              type="password"
              v-model="creds.password"
              :rules="[
                ...$validator.rules.required($t('auth.forms.loginForm.labels.passwordField'))
              ]"
              :label="$t('auth.forms.loginForm.labels.passwordField')"
              :placeholder="$t('auth.forms.loginForm.placeholders.passwordField')" />

            <div class="d-flex justify-end">
              <forgot-password-dialog
                :external-activator="true"
                :show-dialog="showPasswordDialog"
                @show-incorrect-portal="displayIncorrectPortalDialog"
                @close="showPasswordDialog = false" />
            </div>

            <div class="d-flex justify-end mb-8">
              <v-btn
                :ripple="false"
                variant="text"
                size="x-small"
                dense
                class="link forgot-password-btn"
                @click="showPasswordDialog = true">
                {{ $t('auth.forms.loginForm.links.forgotPassword') }}
              </v-btn>
            </div>
          </v-form>

          <div class="entry-form-container pb-8 px-2" v-if="!showVerification">
            <primary-button
              block
              x-large
              class="login-button"
              @click="submit"
              :loading="loading"
              data-testid="login-form-login-button">
              {{ $t('auth.forms.loginForm.buttons.login') }}
            </primary-button>
          </div>
        </v-tabs-window-item>

        <v-tabs-window-item>
          <div class="entry-form-container">
            <v-container fluid>
              <div class="pt-12 px-14 pb-16 mb-6 text-center">
                {{ $t('auth.forms.loginForm.messages.toLogInAsACarrier') }}
              </div>

              <a
                :href="`${lunaUrl}/login`"
                class="primary-button full-width d-flex pa-3 text-center v-btn v-size--x-large font-weight-bold"
                target="_blank"
                rel="noopener">
                {{ $t('auth.forms.loginForm.links.goToSchedulingPortal') }}
                <v-icon class="text-black ml-2">mdi-arrow-right</v-icon>
              </a>
            </v-container>
          </div>
        </v-tabs-window-item>
      </v-tabs-window>
    </template>
  </div>
</template>

<script>
import config from '@/config/config';

/**
 * Login form
 * @displayName Login Form
 */
export default {
  computed: {
    lunaUrl() {
      return config.luna_url;
    }
  },
  props: {
    /**
     * Form header
     */
    header: {
      type: String,
      required: false
    },
    verificationToken: {
      type: String,
      required: false,
      default: ''
    },
    defaultEmail: {
      type: String,
      required: false,
      default: ''
    },
    loginPath: {
      type: String,
      required: false,
      default: 'appointments'
    }
  },
  data() {
    return {
      creds: {
        email: this.defaultEmail,
        password: ''
      },
      portalEmail: '',
      verificationMode: false,
      loggedIn: false,
      loading: false,
      showPasswordDialog: false,
      showVerification: false,
      alertMessage: null,
      alertType: 'success',
      tab: null,
      showIncorrectPortalDialog: false,
      selectedWarehouse: null,
      searchStr: ''
    };
  },
  methods: {
    displayIncorrectPortalDialog(email) {
      this.portalEmail = email;
      this.showIncorrectPortalDialog = true;
    },
    /**
     * Submit the form
     * @public
     * @returns {Promise<void>}
     */
    async submit() {
      if (await this.$isFormValid(this.$refs.form)) {
        this.loading = true;

        const isUserCarrier = await this.$store
          .dispatch('Carriers/doesCarrierExist', this.creds.email)
          .finally(() => {
            this.loading = false;
          });

        if (isUserCarrier) {
          this.displayIncorrectPortalDialog(this.creds.email);
          this.loading = false;
          return;
        }

        this.$store
          .dispatch('Auth/login', this.creds)
          .then(response => {
            if (response?.status !== 200) {
              this.showVerification =
                response?.data?.errorCode === this.novaCore.NovaErrorCode.UnverifiedEmail;
            } else if (localStorage.getItem('access_token')) {
              setTimeout(() => {
                this.loggedIn = true;
              }, 500);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    cancel() {
      /**
       * Emits the close event
       * @event close
       */
      this.$emit('close');
    },
    hotjarIdentify() {
      try {
        if (window.hj && this.$me?.id) {
          const user = this.$me;

          window.hj('identify', user.id, {
            user_role: user.role,
            user_email: user.email,
            org_id: user.orgId,
            org_name: user.orgName,
            org_type: user.orgType
          });
        }
      } catch (error) {
        console.error(`Hotjar identify error ${error}`);
      }
    }
  },
  mounted() {
    if (this.verificationToken) {
      let alertMessage = '';
      let alertType = '';
      this.verificationMode = true;
      this.$store
        .dispatch('Auth/verifyEmail', this.verificationToken)
        .then(response => {
          this.creds.email = response.data.details.email;

          this.mixpanel.identify({ email: this.creds.email });
          this.mixpanel.track(this.mixpanel.events.ACCOUNT.ACTIVATED);

          alertMessage = this.$t(
            'auth.forms.loginForm.messages.yourEmailHasBeenVerifiedYouCanLoginNow'
          );
          alertType = 'success';
          this.notify(alertMessage, alertType, { duration: 8000 });
        })
        .catch(e => {
          alertMessage = e.response?.data?.message ?? this.$t('global.errors.unkownError');
          alertType = 'error';
          this.notify(alertMessage, alertType, { duration: 8000 });
        });
    }
  },
  watch: {
    $me(val) {
      if (val) {
        this.mixpanel.identify(this.$me);
        this.mixpanel.track(this.mixpanel.events.MODULE.USER.LOGIN);

        this.hotjarIdentify();

        if (
          this.$route.query.redirectPath &&
          this.$route.query.redirectPath !== '/login' &&
          this.$route.query.redirectPath !== '/'
        ) {
          this.$router.push({ path: this.$route.query.redirectPath });
        } else {
          if (this.novaCore.isInternalUser(this.$me)) {
            this.$router.push({ name: 'internalHome' });
          } else {
            this.$router.push({ name: this.loginPath });
          }
        }
      }
    }
  }
};
</script>
